import { useFormContext } from 'react-hook-form';
import { ClientInputFormWithCustomFields } from '@/features/public/booking/schemas/clientSchema.ts';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.tsx';
import { TextInput } from '@/components/form/TextInput.tsx';
import TwoInputsRow from '@/components/form/ui/TwoInputsRow.tsx';
import { DateInput } from '@/components/form/DateInput.tsx';
import { Info, Trash } from 'lucide-react';
import DangerIconButton from '@/components/buttons/DangerIconButton.tsx';
import InputError from '@/components/form/base/InputError.tsx';
import ParticipantCustomFieldsForm from '@/features/public/booking/components/ParticipantCustomFieldsForm.tsx';
import ParticipantInsuranceSelect from '@/features/public/booking/components/ParticipantInsuranceSelect.tsx';
import ParticipantTransportSelect from '@/features/public/booking/components/ParticipantTransportSelect.tsx';
import TourOptions from '@/features/shared/booking/components/TourOptions.tsx';
import CustomFieldDefinitionDto from '@/features/dashboard/tourCustomFields/types/_CustomFieldDefinitionDto.ts';
import { BookingScope } from '@/features/shared/booking/types/BookingScope.ts';
import { BookingInsurancesDto } from '@/features/public/booking/types/BookingInsurancesDto.ts';
import { PublicTransportSelectorDto } from '@/features/public/booking/types/PublicTransportSelectorDto.ts';
import { FillerType } from '@/features/public/booking/types/FillerType.ts';
import { useParticipantPricingErrors } from '@/features/public/booking/context/BookingQuoteContext.tsx';
import { placePricingErrors } from '@/features/public/booking/util/pricingErrorPlacement.ts';
import { participantCardId } from '@/features/public/booking/util/participantCardId.ts';

interface ParticipantFormProps {
  index: number;
  remove: (index: number) => void;
  participantsCount: number;
  participantForm: CustomFieldDefinitionDto[];
  scope: BookingScope;
  bookingInsurances?: BookingInsurancesDto | null;
  transport?: PublicTransportSelectorDto | null;
  tourStartDate: string;
}

const ParticipantForm = ({
  index,
  remove,
  participantsCount,
  participantForm,
  scope,
  bookingInsurances,
  transport,
  tourStartDate,
}: ParticipantFormProps) => {
  const clientForm = useFormContext<ClientInputFormWithCustomFields>();

  const {
    register,
    formState: { errors },
    watch,
  } = clientForm;

  const fillerType = watch('fillerType');
  const selectedOptions = watch(`participants.${index}.selectedOptions`);

  const isParticipantFilling =
    fillerType === FillerType.PARTICIPANT && index === 0;
  const hasTransportSelector = !!transport && transport.variants.length > 0;

  const quoteErrors = useParticipantPricingErrors(index);
  const pricingErrors = placePricingErrors(quoteErrors, {
    options: Object.entries(selectedOptions ?? {}).map(([optionId, entry]) => ({
      optionId,
      variantIds: (entry?.optionDto?.variants ?? []).map(
        (variant) => variant.variantId,
      ),
    })),
    hasTransportSelector,
  });

  // Uczestnik nr 0 jest rezerwującym, dopóki formularz wypełnia uczestnik —
  // wtedy nie ma czego usuwać. Ostatni wiersz też zostaje: rezerwacja bez
  // uczestnika nie istnieje.
  const canRemove = !isParticipantFilling && participantsCount > 1;

  return (
    <Card id={participantCardId(index)} className="scroll-mt-4">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Uczestnik {index + 1}</CardTitle>
        {canRemove && (
          <DangerIconButton onClick={() => remove(index)}>
            <Trash />
          </DangerIconButton>
        )}
      </CardHeader>
      <CardContent className="space-y-4 p-4">
        {isParticipantFilling && (
          <p className="flex items-start gap-1.5 text-xs text-neutral-600">
            <Info aria-hidden className="mt-px size-3.5 shrink-0" />
            Imię, nazwisko i datę urodzenia tego uczestnika uzupełniasz powyżej,
            w sekcji „Osoba rezerwująca”.
          </p>
        )}
        {pricingErrors.participant.length > 0 && (
          <div className="flex flex-col">
            {pricingErrors.participant.map((error) => (
              <InputError key={error} error={error} />
            ))}
          </div>
        )}
        <TwoInputsRow>
          <TextInput
            label="Imię"
            register={register(`participants.${index}.firstName`)}
            placeholder="Wpisz imię..."
            required
            error={errors.participants?.[index]?.firstName?.message}
            disabled={isParticipantFilling}
            // Uczestnik to zwykle inna osoba niż rezerwujący — podpowiedź
            // z autouzupełniania wstawiłaby tu imię rodzica.
            autoComplete="off"
            enterKeyHint="next"
          />
          <TextInput
            label="Nazwisko"
            register={register(`participants.${index}.lastName`)}
            placeholder="Wpisz nazwisko..."
            required
            error={errors.participants?.[index]?.lastName?.message}
            disabled={isParticipantFilling}
            autoComplete="off"
            enterKeyHint="next"
          />
        </TwoInputsRow>
        <DateInput
          label="Data urodzenia"
          dateFieldName={`participants.${index}.birthDate`}
          control={clientForm.control}
          required
          error={
            errors.participants?.[index]?.birthDate?.message ??
            // Gdy formularz wypełnia uczestnik, to pole jest tylko odbiciem —
            // komunikat o wieku stawiamy przy tej dacie, którą da się zmienić.
            (isParticipantFilling ? undefined : pricingErrors.birthDate[0])
          }
          disabled={isParticipantFilling}
        />
        <ParticipantTransportSelect
          index={index}
          transport={transport}
          tourStartDate={tourStartDate}
          quoteErrors={pricingErrors.transport}
        />
        <TourOptions
          index={index}
          scope={scope}
          optionErrors={pricingErrors.byOptionId}
        />
        <ParticipantCustomFieldsForm
          index={index}
          participantForm={participantForm}
        />
        <ParticipantInsuranceSelect
          index={index}
          bookingInsurances={bookingInsurances}
        />
      </CardContent>
    </Card>
  );
};

export default ParticipantForm;
